


// apps/admin-ui/src/config/dashboardWidgets.ts
// Dashboard widget configuration with layouts for all 12 roles
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-01: Added pending-withdrawals widget for treasury dashboard
// UPDATED 2026-02-01: Changed pending-mints title to "Deposit Activity" since deposits auto-complete
// UPDATED 2026-02-02: Added dex-onramp-activity widget for crypto funding monitoring
// UPDATED 2026-02-04: Added contract-rules-summary widget
// UPDATED 2026-02-04: Enabled pending-reviews widget (removed isMock)
// UPDATED 2026-02-07: Added audit-events widget for security dashboard (live KYC audit log)

import type { Permission, Role } from '../utils/rbac';

// =============================================================================
// TYPES
// =============================================================================

export interface WidgetConfig {
  id: string;
  title: string;
  permission: Permission;
  refreshInterval: number;
  defaultExpanded: boolean;
  priority: number;
  size: 'small' | 'medium' | 'large' | 'full';
  isMock?: boolean;
}

export interface RoleDashboardLayout {
  title: string;
  showSystemStatus: boolean;
  showExecutiveOverview?: boolean;
  widgetIds: string[];
  drillDowns?: Record<string, string>;
}

// =============================================================================
// WIDGET DEFINITIONS
// =============================================================================

export const DASHBOARD_WIDGETS: WidgetConfig[] = [
  // SYSTEM STATUS
  {
    id: 'system-status',
    title: 'System Status',
    permission: 'dashboard:view_system_status',
    refreshInterval: 5,
    defaultExpanded: true,
    priority: 1,
    size: 'full',
  },

  // KEY METRICS
  {
    id: 'transaction-volume',
    title: 'Transaction Volume',
    permission: 'dashboard:view_transactions',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'fees-collected',
    title: 'Fees Collected',
    permission: 'dashboard:view_fees',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'reserve-ratio',
    title: 'Reserve Ratio',
    permission: 'dashboard:view_reserve',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'system-uptime',
    title: 'System Uptime',
    permission: 'dashboard:view_performance',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'peg-status',
    title: 'USD Peg Status',
    permission: 'dashboard:view_reserve',
    refreshInterval: 10,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },

  // TREASURY
  {
    id: 'treasury-balance',
    title: 'Treasury Balance',
    permission: 'reserve:view_balances',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 3,
    size: 'medium',
  },
  {
    id: 'circulation',
    title: 'CXXR in Circulation',
    permission: 'dashboard:view_reserve',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 3,
    size: 'medium',
  },
  {
    id: 'reserve-composition',
    title: 'Reserve Composition',
    permission: 'dashboard:view_reserve',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 3,
    size: 'full',
    isMock: true,
  },
  {
    id: 'pending-mints',
    title: 'Deposit Activity',  // CHANGED: Deposits auto-complete, no approval needed
    permission: 'dashboard:view_mint_requests',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
  },
  // Pending Withdrawals widget - these DO need approval
  {
    id: 'pending-withdrawals',
    title: 'Pending Withdrawal Requests',
    permission: 'dashboard:view_mint_requests', // same permission as mints
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
  },
  // DEX OnRamp Activity - Crypto funding via DEX (NEW)
  {
    id: 'dex-onramp-activity',
    title: 'Crypto Funding (DEX)',
    permission: 'dashboard:view_mint_requests', // reuse existing permission
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
  },

  // CHARTS
  {
    id: 'fee-distribution',
    title: 'Fee Distribution',
    permission: 'dashboard:view_fees',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 3,
    size: 'medium',
  },
  {
    id: 'volume-chart',
    title: 'Daily Transaction Volume',
    permission: 'dashboard:view_volume',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 3,
    size: 'medium',
  },

  // COMPLIANCE
  {
    id: 'kyc-pending',
    title: 'KYC Pending',
    permission: 'dashboard:view_kyc_queue',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'aml-alerts',
    title: 'AML Alerts',
    permission: 'dashboard:view_aml_alerts',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
  },
  {
    id: 'kyc-queue-preview',
    title: 'KYC Queue',
    permission: 'dashboard:view_kyc_queue',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
  },
  {
    id: 'kyc-throughput',
    title: 'KYC Throughput',
    permission: 'dashboard:view_kyc_queue',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 3,
    size: 'full',
  },

  // SECURITY
  {
    id: 'vulnerabilities',
    title: 'Open Vulnerabilities',
    permission: 'dashboard:view_vulnerabilities',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
    isMock: true,
  },
  {
    id: 'pentest-status',
    title: 'Pen Test Status',
    permission: 'dashboard:view_vulnerabilities',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
    isMock: true,
  },
  {
    id: 'security-alerts',
    title: 'Security Alerts',
    permission: 'dashboard:view_security_alerts',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
    isMock: true,
  },
  {
    id: 'audit-events',
    title: 'Audit Events',
    permission: 'system:view_audit_logs',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 3,
    size: 'full',
  },
  {
    id: 'performance-metrics',
    title: 'Performance Metrics',
    permission: 'dashboard:view_performance',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'medium',
  },

  // CONTRACT AUDITOR
  {
    id: 'anchor-chain',
    title: 'Anchor Chain Status',
    permission: 'dashboard:view_anchors',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 3,
    size: 'full',
    isMock: true,
  },
  {
    id: 'pending-reviews',
    title: 'Pending Smart Contract Reviews',
    permission: 'contracts:view',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 2,
    size: 'medium',
  },
  {
    id: 'last-deployment',
    title: 'Last Deployment',
    permission: 'contracts:view',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 2,
    size: 'small',
    isMock: true,
  },
  {
    id: 'deployments',
    title: 'Recent Deployments',
    permission: 'contracts:view',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
    isMock: true,
  },

  // CONTRACT RULES (summary of key business parameters)
  {
    id: 'contract-rules-summary',
    title: 'Contract Rules',
    permission: 'contracts:view',
    refreshInterval: 0,  // Static data — no polling
    defaultExpanded: true,
    priority: 3,
    size: 'full',
  },

  // GOVERNANCE
  {
    id: 'governance-proposals',
    title: 'Active Proposals',
    permission: 'dashboard:view_governance',
    refreshInterval: 300,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
    isMock: true,
  },

  // ACCOUNTING
  {
    id: 'transactions-preview',
    title: 'Recent Transactions',
    permission: 'dashboard:view_transactions',
    refreshInterval: 30,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
  },

  // AUDIT (NEW)
  {
    id: 'attestation-status',
    title: 'Monthly Attestation',
    permission: 'dashboard:view_attestation',
    refreshInterval: 3600,
    defaultExpanded: true,
    priority: 2,
    size: 'medium',
    isMock: true,
  },
  {
    id: 'audit-reports',
    title: 'Audit Reports',
    permission: 'dashboard:view_audit_reports',
    refreshInterval: 3600,
    defaultExpanded: true,
    priority: 4,
    size: 'full',
    isMock: true,
  },
  {
    id: 'reserve-verification',
    title: 'Reserve Verification',
    permission: 'dashboard:view_asset_holdings',
    refreshInterval: 300,
    defaultExpanded: true,
    priority: 3,
    size: 'medium',
    isMock: true,
  },

  // CUSTODIAN (NEW)
  {
    id: 'asset-holdings',
    title: 'Segregated Asset Holdings',
    permission: 'dashboard:view_asset_holdings',
    refreshInterval: 60,
    defaultExpanded: true,
    priority: 2,
    size: 'full',
    isMock: true,
  },
];

// =============================================================================
// ROLE-SPECIFIC LAYOUTS (all 12 roles)
// =============================================================================

export const ROLE_LAYOUTS: Record<Role, RoleDashboardLayout> = {
  // ─────────────────────────────────────────────────────────────────────────
  // INTERNAL - GOVERNANCE
  // ─────────────────────────────────────────────────────────────────────────
  admin: {
    title: 'Admin Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: true,
    widgetIds: [
      'reserve-ratio', 'peg-status', 'transaction-volume', 'fees-collected',
      'kyc-pending', 'aml-alerts', 'vulnerabilities',
      'volume-chart', 'fee-distribution',
      'pending-mints', 'pending-withdrawals', 'dex-onramp-activity', 'governance-proposals',
      'audit-events',
    ],
    drillDowns: {
      'kyc-pending': '/admin/kyc',
      'aml-alerts': '/admin/aml',
      'pending-mints': '/admin/mints',
      'pending-withdrawals': '/admin/withdrawals',
      'dex-onramp-activity': '/admin/dex/onramps',
      'vulnerabilities': '/admin/security/vulns',
      'governance-proposals': '/admin/governance',
    },
  },

  council: {
    title: 'Council Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: true,
    widgetIds: [
      'reserve-ratio', 'peg-status', 'transaction-volume', 'fees-collected',
      'governance-proposals', 'contract-rules-summary', 'audit-reports',
      'audit-events',
    ],
    drillDowns: {
      'governance-proposals': '/admin/governance',
      'audit-reports': '/admin/audit/reports',
    },
  },

  board_director: {
    title: 'Board Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: true,
    widgetIds: [
      'reserve-ratio', 'peg-status', 'transaction-volume', 'fees-collected',
      'fee-distribution', 'governance-proposals', 'audit-reports',
    ],
    drillDowns: {
      'governance-proposals': '/admin/governance',
      'audit-reports': '/admin/audit/reports',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INTERNAL - ADMINISTRATION
  // ─────────────────────────────────────────────────────────────────────────
  treasury: {
    title: 'Treasury Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: true,
    widgetIds: [
      'reserve-ratio', 'peg-status', 'treasury-balance', 'circulation',
      'reserve-composition', 'pending-mints', 'pending-withdrawals', 
      'dex-onramp-activity',  // NEW: Crypto funding widget
      'anchor-chain',
    ],
    drillDowns: {
      'pending-mints': '/admin/mints',
      'pending-withdrawals': '/admin/withdrawals',
      'dex-onramp-activity': '/admin/dex/onramps',  // NEW
    },
  },

  compliance: {
    title: 'Compliance Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'kyc-pending', 'aml-alerts', 'reserve-ratio',
      'kyc-queue-preview', 'audit-reports',
    ],
    drillDowns: {
      'kyc-pending': '/admin/kyc',
      'aml-alerts': '/admin/aml',
      'audit-reports': '/admin/audit/reports',
    },
  },

  accounting: {
    title: 'Accounting Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'transaction-volume', 'fees-collected', 'reserve-ratio',
      'fee-distribution', 'volume-chart', 'transactions-preview',
    ],
    drillDowns: {
      'transactions-preview': '/admin/transactions',
    },
  },

  security_admin: {
    title: 'Security Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'vulnerabilities', 'pentest-status', 'system-uptime',
      'performance-metrics', 'security-alerts',
      'audit-events',
    ],
    drillDowns: {
      'vulnerabilities': '/admin/security/vulns',
      'security-alerts': '/admin/security/alerts',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // EXTERNAL - CONTRACTORS
  // ─────────────────────────────────────────────────────────────────────────
  contract_auditor: {
    title: 'Contract Auditor Dashboard',
    showSystemStatus: false,
    showExecutiveOverview: false,
    widgetIds: [
      'pending-reviews', 'last-deployment', 'contract-rules-summary',
      'anchor-chain', 'performance-metrics', 'deployments', 'audit-reports',
    ],
    drillDowns: {
      'pending-reviews': '/admin/contracts/reviews',
      'deployments': '/admin/contracts',
      'audit-reports': '/admin/audit/reports',
    },
  },

  cpa_auditor: {
    title: 'CPA Auditor Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'reserve-ratio', 'peg-status', 'attestation-status',
      'reserve-composition', 'fee-distribution',
      'contract-rules-summary', 'reserve-verification', 'audit-reports',
    ],
    drillDowns: {
      'audit-reports': '/admin/audit/reports',
      'attestation-status': '/admin/audit/attestation',
    },
  },

  custodian: {
    title: 'Custodian Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'reserve-ratio', 'peg-status',
      'asset-holdings', 'reserve-verification',
    ],
    drillDowns: {
      'asset-holdings': '/admin/custody/holdings',
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INTERNAL - OPERATIONS (no Admin UI access)
  // ─────────────────────────────────────────────────────────────────────────
  csr: {
    title: '',
    showSystemStatus: false,
    widgetIds: [],
  },
  csr_supervisor: {
    title: 'CSR Supervisor Dashboard',
    showSystemStatus: true,
    showExecutiveOverview: false,
    widgetIds: [
      'kyc-pending', 'aml-alerts',
      'kyc-throughput',
    ],
    drillDowns: {
      'kyc-pending': '/admin/kyc',
      'aml-alerts': '/admin/aml',
    },
  },
};

// =============================================================================
// EXECUTIVE OVERVIEW CONFIG
// =============================================================================

export const EXECUTIVE_ROLES: Role[] = ['admin', 'council', 'board_director', 'treasury'];

export function showsExecutiveOverview(role: Role): boolean {
  return EXECUTIVE_ROLES.includes(role);
}

// =============================================================================
// HELPERS
// =============================================================================

export function getRoleLayout(role: Role): RoleDashboardLayout | null {
  const layout = ROLE_LAYOUTS[role];
  if (!layout || layout.widgetIds.length === 0) return null;
  return layout;
}

export function getWidgetsForRole(role: Role): WidgetConfig[] {
  const layout = ROLE_LAYOUTS[role];
  if (!layout || layout.widgetIds.length === 0) return [];

  return layout.widgetIds
    .map(id => DASHBOARD_WIDGETS.find(w => w.id === id))
    .filter((w): w is WidgetConfig => w !== undefined);
}

export function getWidgetById(id: string): WidgetConfig | undefined {
  return DASHBOARD_WIDGETS.find(w => w.id === id);
}

export function isWidgetMock(widgetId: string): boolean {
  const widget = getWidgetById(widgetId);
  return widget?.isMock ?? false;
}

export function getWidgetsByPriority(widgets: WidgetConfig[]): WidgetConfig[] {
  return [...widgets].sort((a, b) => a.priority - b.priority);
}

export function groupWidgetsBySize(widgets: WidgetConfig[]): {
  small: WidgetConfig[];
  medium: WidgetConfig[];
  large: WidgetConfig[];
  full: WidgetConfig[];
} {
  return {
    small: widgets.filter(w => w.size === 'small'),
    medium: widgets.filter(w => w.size === 'medium'),
    large: widgets.filter(w => w.size === 'large'),
    full: widgets.filter(w => w.size === 'full'),
  };
}

export function getDrillDownUrl(role: Role, widgetId: string): string | null {
  const layout = ROLE_LAYOUTS[role];
  return layout?.drillDowns?.[widgetId] ?? null;
}



